import { Box, IconButton, Typography } from '@mui/material'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {
  displayMultipleTitle,
  getItemIdValue,
  getSanitizedValue,
  toCamelCase,
} from '../../../Helpers/Util'
import {
  IFieldTitleValue,
  IKeyValue,
  ISubmitCtaBtnProps,
  scheduleTimeType,
} from '../../../Models/CommonListHeadingModal'
import RadioInputComponent from '../../Shared/RadioInput'
import CONSTANTS from '../../../Constants'
import { FieldValues, FormProvider } from 'react-hook-form'
import React, { lazy, Suspense } from 'react'
import { Loader, MUIButton, MUICheckbox, ChipButton } from '../../Shared'
import {
  DisplayPinnedItemDetails,
  DisplayTabDetailAndFormProps,
  GetItemModalSubTitle,
  RenderArrayValue,
  RenderFilterList,
} from '../../../Models/CategoryFormData'
import { ComplexFieldView } from '../../ComplexView'
import Styles from './CommonHelperFunctions.module.scss'
import FloatingAddButton from '../../../Models/FloatingButtonModel/FloatingAddButton'
import { handleScheduleTimeModal } from '../../../Helpers/DataCategoryUtils'
import ItemCount from '../ItemCount'
import { CustomTooltip } from '../../Shared/CustomToolTip/CustomToolTip'
import { TooltipText } from '../../Shared/TooltipText/TooltipText'
import headerStyles from '../../Atoms/HeaderComponent/HeaderComponent.module.scss'
import { Link } from 'react-router-dom'
import { RedirectIcon } from '../../Icons'
import WarningIcon from '../../Icons/WarningIcon'

const DynamicFormCreation = lazy(() => import('../../Atoms/DynamicFormCreation'))
const ItemDetailAccordion = lazy(() => import('../../Shared/ItemDetailAccordion/index'))
const LazyLoader = lazy(() => import('../../Shared/Loader/LazyLoader'))

export const getTitleValue = (title: string, value: string, required?: boolean) => {
  return (
    <>
      <Box color='#6A6C6D' width='192px'>
        {title}
        {required && <span style={{ color: '#D4351C', margin: 0 }}>*</span>}
      </Box>
      <Box
        className='font-semibold word-break-all'
        width='calc(100% - 192px)'
        paddingLeft='0.75rem'
        dangerouslySetInnerHTML={{
          __html: getSanitizedValue(Array.isArray(value) ? displayMultipleTitle(value) : value),
        }}
      />
    </>
  )
}
export const displayFieldTitleValue = (data: IFieldTitleValue, isLastField: boolean) => {
  const { title, value, required } = data

  return (
    <Box
      data-testid={toCamelCase(title)}
      key={title}
      width='100%'
      display='flex'
      alignItems='baseline'
      fontSize='14px'
      borderBottom={isLastField ? 0 : '1px solid rgba(0, 0, 0, 0.16)'}
      padding='1.25rem 0.75rem'
    >
      {getTitleValue(title, value, required)}
    </Box>
  )
}
export const displayParameterData = (formData: IFieldTitleValue[]) => {
  if (formData?.length === 0) return null
  return formData?.map((data: any, index: number) => {
    if (data?.parameterRef?.length > 0) {
      return (
        <div
          key={data?.fieldName}
          style={{
            borderBottom: index === formData?.length - 1 ? 'none' : '1px solid rgba(0, 0, 0, 0.16)',
            width: '100%',
          }}
        >
          <h3 className='complex-type-field-title'>{data.title}</h3>
          <ComplexFieldView data={data.parameterRef} />
        </div>
      )
    } else {
      return displayFieldTitleValue(data, index === formData.length - 1)
    }
  })
}
export const displayTabs = (
  dataTabs: IKeyValue[],
  selectedTab: IKeyValue,
  onClick: (tab: IKeyValue) => void,
  checkContainerListingTab = false,
) => {
  if (
    (dataTabs?.length === 0 || (dataTabs.length === 1 && selectedTab?.key === 'default')) &&
    selectedTab?.key === ''
  )
    return null
  return dataTabs.map((tab) => {
    const isSelectedTab = tab.key === selectedTab.key
    const hideTab =
      checkContainerListingTab &&
      !CONSTANTS.MASTER_LISTING_TABS.some(
        (item) => item.value.toLowerCase() === tab.value.toLowerCase(),
      )

    return (
      <div
        data-testid={`tab_${toCamelCase(tab.key)}`}
        className='font-semibold'
        key={toCamelCase(tab.key)}
        aria-hidden='true'
        onClick={(e) => {
          e.preventDefault()
          onClick(tab)
        }}
        style={{
          padding: '0.75rem 1.5rem',
          color: isSelectedTab ? '#402080' : '#3E3F40',
          background: isSelectedTab ? '#ECE9F2' : 'transparent',
          borderRadius: isSelectedTab ? '0.25rem 0.25rem 0 0' : 0,
          borderBottom: isSelectedTab ? '2px solid #402080' : 0,
          cursor: 'pointer',
          display: hideTab ? 'none' : 'block',
        }}
      >
        {tab.value}
      </div>
    )
  })
}
export const getCheckboxOrRadioComponent = (
  isSelectedItemSingleSelect: boolean,
  radioPropsData: any = {},
  checkboxPropsData: any = {},
) => {
  if (!isSelectedItemSingleSelect) return <MUICheckbox {...checkboxPropsData} />
  return <RadioInputComponent {...radioPropsData} />
}
export const renderReadOnlyView = (
  formData: IFieldTitleValue[],
  isBtnDisable: boolean,
  nonMandatoryFields?: string[],
) => {
  return formData?.map((item, idx) => {
    const { title, value, fieldName, required } = item
    let isFieldRequired = required
    if (nonMandatoryFields?.length && nonMandatoryFields?.includes(fieldName))
      isFieldRequired = false
    const isLastItem = idx === formData.length - 1
    const borderStyle = isBtnDisable && isLastItem ? { borderBottom: 0 } : {}

    return (
      <div key={fieldName} className='d-flex align-items-start row-title-value' style={borderStyle}>
        {getTitleValue(title, value, isFieldRequired)}
      </div>
    )
  })
}
export const displayTabDetailAndForm = <T extends FieldValues>({
  isBtnDisable,
  selectedTabIndex,
  methods,
  conditionalFieldsToShow,
  definitionInfo,
  formData,
  formDetail,
  pageType,
  module,
}: DisplayTabDetailAndFormProps<T>) => {
  const { VIEW } = CONSTANTS.PAGE_TYPE

  if (pageType === VIEW) {
    return renderReadOnlyView(formData, isBtnDisable)
  }

  if (selectedTabIndex === -1) return null

  const selectedForm = definitionInfo?.data?.tabs?.[selectedTabIndex]
  const selectedFormData = selectedForm?.tabContent?.formData
  const selectedFormLabel = selectedFormData?.[0]?.label

  return (
    <FormProvider {...methods}>
      <Suspense fallback={<Loader />} key='header_title'>
        <div className='w-100' style={{ marginBottom: '-1.5rem' }}>
          <DynamicFormCreation
            categoryFormData={formDetail}
            formData={selectedFormData}
            formLabel={selectedFormLabel}
            conditionalFieldsToShow={conditionalFieldsToShow}
            module={module}
          />
        </div>
      </Suspense>
    </FormProvider>
  )
}
export const showSubmitCtaBtn = ({
  pageType,
  disabled,
  onClick,
  showSecondaryCta,
  shouldDisableSecondaryCta,
  onSecondaryCtaClick,
  secondaryCtaLabel,
  submitCtaTitle,
}: ISubmitCtaBtnProps) => {
  const { VIEW } = CONSTANTS.PAGE_TYPE
  if (pageType === VIEW) return null
  return (
    <div className='footer'>
      <div className='submit-btn-wrapper'>
        <div className='submit-btn'>
          <div className='d-flex align-items-center gap-1'>
            {showSecondaryCta ? (
              <FloatingAddButton
                size='large'
                label={secondaryCtaLabel}
                width={126}
                variant='outlined'
                disabled={shouldDisableSecondaryCta}
                onClick={onSecondaryCtaClick}
                testID='footer-add-cta'
              />
            ) : null}
            <MUIButton
              size='large'
              type={'button'}
              label={submitCtaTitle ?? 'Save'}
              disabled={disabled}
              variant='contained'
              width={150}
              handleClickCB={(e: any) => onClick(e)}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
export const getFilterTitle = (selectedTab: string, filterType: 'type' | 'layout') => {
  return (
    <Typography
      sx={{
        fontSize: '0.875rem',
        color: '#697282',
        lineHeight: '26px',
        paddingBottom: '0.75rem',
      }}
    >
      {`Choose a ${selectedTab} ${filterType} to filter your search results`}
    </Typography>
  )
}
export const getItemModalSubTitle = ({
  selectedTab,
  subtitleType,
  paddingBottom,
  suffix,
}: GetItemModalSubTitle) => {
  const searchSuffix = suffix ? ` ${suffix}` : ''
  return (
    <Typography
      sx={{
        fontSize: '1.125rem',
        color: '#191D23',
        lineHeight: '28px',
        fontWeight: 600,
        paddingBottom: paddingBottom ?? '0.25rem',
        textTransform: 'capitalize',
      }}
    >
      {`${selectedTab} ${subtitleType}${searchSuffix}`}
    </Typography>
  )
}
export const renderFilterList = ({
  filterList,
  onClick,
  selectedValue,
  keyType = 'key',
  shouldHover = false,
}: RenderFilterList) => {
  if (filterList.length === 0) return null

  return (
    <ul className={Styles['filter-item-list']}>
      {filterList.map((item) => {
        const isActive = item[keyType] === selectedValue
        return (
          <li
            data-testid={`${item[keyType]?.replaceAll(/\s/g, '')}Filter`}
            key={item.key}
            aria-hidden='true'
            className={`${isActive ? Styles['active'] : ''} ${
              shouldHover ? Styles['hover-item'] : ''
            }`}
            onClick={() => onClick(item[keyType])}
          >
            <span>{item.value}</span>
          </li>
        )
      })}
    </ul>
  )
}
export const renderCsvDetailView = (
  detail: Array<Record<string, any>>,
  includeBorderBottom = true,
) => {
  return detail.map((item, idx) => {
    const { key, value } = item
    let valueTextColor = '#101828'
    if (key.toLowerCase() === 'success count') valueTextColor = '#00A63E'
    if (key.toLowerCase() === 'failure count') valueTextColor = '#E7000B'

    return (
      <div
        key={toCamelCase(key)}
        className='d-flex align-items-start'
        style={{
          padding: '1rem 1.5rem',
          borderBottom:
            idx === detail.length - 1 && includeBorderBottom ? 0 : '1px solid rgba(0, 0, 0, 0.16)',
          color: '#4A5565',
          lineHeight: '20px',
          gap: '1.5rem',
        }}
      >
        <Typography textAlign='left' fontSize='14px' width='192px'>
          {key}
        </Typography>
        <Typography
          color={valueTextColor}
          fontWeight='500'
          textAlign='left'
          width='calc(100% - 192px)'
          fontSize='14px'
        >
          {Array.isArray(value) ? displayMultipleTitle(value) : value}
        </Typography>
      </div>
    )
  })
}
export const displayPinnedItemDetails = ({
  containerErrors,
  handleScheduleClick,
  pageType,
  pinnedItemsList,
  scheduleItemLists,
  setScheduleItemModal,
  setSelectedItemModule,
  layoutType,
  pinnedLoadingMore,
  visibleCountItems,
  totalCountItems,
  isCollapsed = false,
  onToggleCollapse,
}: DisplayPinnedItemDetails) => {
  const { VIEW } = CONSTANTS.PAGE_TYPE
  if (visibleCountItems < totalCountItems || pinnedItemsList.length === 0) return null
  return (
    <div className='asset-container' style={{ marginTop: '1rem' }}>
      <div className='asset-title'>
        <div className='category-title'>
          {onToggleCollapse && (
            <IconButton
              size='small'
              onClick={onToggleCollapse}
              aria-label={isCollapsed ? 'expand pinned section' : 'collapse pinned section'}
              sx={{ mr: 0.5 }}
            >
              {isCollapsed ? <ChevronRightIcon /> : <ExpandMoreIcon />}
            </IconButton>
          )}
          Pinned Item
          <ItemCount value={pinnedItemsList.length} />
        </div>
      </div>
      {!isCollapsed && (
        <div data-testid='pin-item-detail-list' style={{ padding: '1rem 0.75rem 0.75rem' }}>
          {pinnedItemsList.map((pinnedItem: any, index: any) => {
            return (
              <Suspense fallback={<Loader />} key={`pinnedItem-${pinnedItem?.id}`}>
                <ItemDetailAccordion
                  pageType={pageType}
                  hideChildrenIcon={{
                    position: true,
                    scheduleIcon: true,
                  }}
                  disabled={true}
                  isPinnedItemDisabled={pageType === VIEW}
                  key={pinnedItem?.id}
                  index={index}
                  data={pinnedItem}
                  selectedAssetDetailsList={pinnedItemsList}
                  layoutType={layoutType}
                  handleScheduleClick={(id: string | number, type: scheduleTimeType) => {
                    if (type === 'update') {
                      handleScheduleTimeModal({
                        id,
                        item: pinnedItem,
                        setScheduleItemModal,
                        setSelectedItemModule,
                      })
                    } else {
                      handleScheduleClick(scheduleItemLists, pinnedItem, 'remove')
                    }
                  }}
                  type='manualPin'
                  containerError={
                    containerErrors?.find(
                      (error: { containerId: string; contentId: any }) =>
                        error?.containerId === getItemIdValue(pinnedItem) ||
                        error?.contentId === getItemIdValue(pinnedItem),
                    ) ?? null
                  }
                />
              </Suspense>
            )
          })}
          {pinnedLoadingMore && <LazyLoader />}
        </div>
      )}
    </div>
  )
}
export const getIrisStatusLabel = (status?: string) => {
  if (!status) return ''
  const upperStatus = status.toUpperCase()
  if (upperStatus === 'ACTIVE') return 'Active'
  if (upperStatus === 'INACTIVE') return 'Inactive'
  return ''
}

export const getIrisChip = (status?: string) => {
  const label = getIrisStatusLabel(status)
  if (!label) return null
  const upperStatus = status?.toUpperCase()
  const chipType = upperStatus === 'INACTIVE' ? 'secondary' : 'success'
  return (
    <>
      <p className={headerStyles.irisLabel}>IRIS</p>
      <span className={headerStyles.irisDot} />
      <ChipButton type={chipType} label={label} style={{ padding: '0px 8px', marginLeft: '4px' }} />
    </>
  )
}

interface SegmentTooltipContentProps {
  value: any[]
  toURL: string
  idKeyToCheck: string
  isSegment: boolean
  tooltipModalTitle?: string
  onClick?: (event: React.MouseEvent) => void
}

const SegmentTooltipContent = ({
  value,
  toURL,
  idKeyToCheck,
  isSegment,
  tooltipModalTitle,
  onClick,
}: SegmentTooltipContentProps) => {
  const items = isSegment ? value : value.slice(1)
  return (
    <div>
      <div className={headerStyles.boxHeader}>
        <WarningIcon />
        <p>{`Additional ${tooltipModalTitle} (${value.length - 1})`}</p>
      </div>
      <div className={headerStyles.boxContent}>
        {items.map((item) => (
          <Link
            key={item?.segmentId}
            target='_blank'
            to={`${toURL}/${item?.[idKeyToCheck]}`}
            onClick={(event) => onClick?.(event)}
            className={headerStyles.segmentRow}
          >
            <span className={headerStyles.segmentValue}>
              <RedirectIcon />
              <TooltipText
                disableInteractive
                text={item.value}
                placement='right'
                length={30}
                slotProps={{
                  tooltip: {
                    sx: {
                      padding: '0.25rem 0.75rem',
                      position: 'relative',
                      left: '0.8rem',
                      fontSize: '0.75rem',
                      fontWeight: '500',
                      lineHeight: '1.5rem',
                      color: '#402080',
                      bgcolor: '#ECE9F2',
                      maxWidth: '300px',
                      width: 'max-content',
                      zIndex: 1400,
                      '& .MuiTooltip-arrow': { color: '#ECE9F2' },
                    },
                  },
                }}
              />
            </span>
            <div className={headerStyles.irisStatus}>{getIrisChip(item?.irisSegmentStatus)}</div>
          </Link>
        ))}
      </div>
    </div>
  )
}
export const displayArrayItemValue = (value: string, length: number, toolTipWidth?: string) => {
  if (value?.length <= length) return value
  return (
    <CustomTooltip
      title={value}
      placement='bottom'
      arrow
      disableInteractive
      slotProps={{
        tooltip: {
          sx: {
            padding: '0.25rem 0.75rem',
            fontSize: '0.75rem',
            fontWeight: '500',
            lineHeight: '1.5rem',
            color: '#402080',
            bgcolor: '#ECE9F2',
            maxWidth: toolTipWidth ?? '238px',
            width: 'max-content',
            '& .MuiTooltip-arrow': {
              color: '#ECE9F2',
            },
          },
        },
      }}
    >
      <p style={{ margin: 0, cursor: 'pointer' }}>{value?.slice(0, length)}...</p>
    </CustomTooltip>
  )
}
export const renderArrayValue = ({
  idKeyToCheck,
  pageURI,
  value,
  maxCount,
  title,
  onClick,
  testId,
  tooltipModalTitle,
}: RenderArrayValue) => {
  if (!Array.isArray(value) || value.length === 0) return 'NA'

  const isSegment = title?.toLowerCase() === 'segment'
  const isContainer = title?.toLowerCase() === 'containers'
  const displayTooltip = isSegment || isContainer
  const toURL = displayTooltip ? `${pageURI}/${CONSTANTS.PAGE_TYPE.VIEW}` : ''

  const tooltipTitle = displayTooltip ? (
    <SegmentTooltipContent
      value={value}
      toURL={toURL}
      idKeyToCheck={idKeyToCheck}
      isSegment={isSegment}
      tooltipModalTitle={tooltipModalTitle}
      onClick={onClick}
    />
  ) : (
    value.map((item) => item?.value).join(', ')
  )

  return (
    <div className='d-flex align-items-center' style={{ gap: '6px' }}>
      {displayTooltip ? (
        <>
          <Link
            target='_blank'
            to={`${toURL}/${(value?.[0] as any)?.[idKeyToCheck]}`}
            className={headerStyles.tooltipWithBg}
            data-testid={testId}
            onClick={(event) => onClick?.(event)}
          >
            <RedirectIcon /> {displayArrayItemValue(value?.[0]?.value, maxCount || 25)}
          </Link>
          {value.length === 1 && isSegment && getIrisChip((value?.[0] as any)?.irisSegmentStatus)}
        </>
      ) : (
        <p className={headerStyles.segmentPara}>
          {displayArrayItemValue(value?.[0]?.value, maxCount || 25)}
        </p>
      )}

      {value.length > 1 && (
        <CustomTooltip
          title={tooltipTitle}
          placement='bottom'
          slotProps={{
            tooltip: {
              sx: {
                padding: '2',
                fontSize: '0.75rem',
                fontWeight: '500',
                lineHeight: '1.5rem',
                color: '#402080',
                bgcolor: '#FFFFFF',
                maxWidth: '285px',
                maxHeight: '255px',
                border: '1px solid #C4BAD8',
                borderRadius: '12px',
              },
            },
          }}
        >
          <div className={`${headerStyles.moreSegment}`}>
            <WarningIcon />
            {`+${value.length - 1} others`}
          </div>
        </CustomTooltip>
      )}
    </div>
  )
}
